import { useEffect, useRef, useState } from "react";
import stringSimilarity from "string-similarity";
import VoiceRecognitionBar from "../design/VoiceRecognitionBar";
import axios from "axios";
import { useRecoilState } from "recoil";
import { readingProblemTypeAtom } from "../atoms/readingProblemTypeAtom";
import { base64ToBlob } from "./ReadingMain";

const NounQues = ({
  listening,
  transcript,
  resetTranscript,
  toggleListening,
  setCurSentence,
  sentence,
  sentenceLenght,
}: {
  listening: boolean;
  transcript: string;
  resetTranscript: () => void;
  toggleListening: () => void;
  setCurSentence: React.Dispatch<React.SetStateAction<number>>;
  sentence: string;
  sentenceLenght: number;
}) => {
  const [quesNum, setQuesNum] = useState(0);
  const [isVoiceRecognitionRight, setIsVoiceRecognitionRight] = useState(0); // 0 아무텍스트도 없음, 1 잘하셨어요 -1 다시한번 생각해보세요
  const [problemType, setProblemType] = useRecoilState(readingProblemTypeAtom);
  const [nounQues, setNounQues] = useState<string[]>([]);
  const [nounQuesChangedData, setNounQuesChangedData] = useState<string[]>([]);
  const [numberOfIncorrectNounQuestions, setNumberOfIncorrectNounQuestions] =
    useState(0);
  const targetQues = nounQuesChangedData[quesNum];
  const numberOfQue = nounQuesChangedData.length;

  const audioElement = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    makeNounQues();
  }, [sentence]);

  useEffect(() => {
    if (nounQues.length > 0) {
      // 데이터를 이어 붙이고, 반대로도 이어 붙인 결과 생성
      const joined = nounQues.join(", "); // "사자 쥐 사과"
      const reversed = [...nounQues].reverse().join(" "); // "사과 쥐 사자"
      setNounQuesChangedData([joined, reversed]);
    }
  }, [nounQues]);

  const makeNounQues = async () => {
    const res = await axios.get(
      "https://uvlqtxcfrh.us14.qoddiapp.com/reading/nouns",
      { params: { sentence } }
    );

    setNounQues(res.data.output);
  };

  const onSubmitTranscript = () => {
    const similarity = stringSimilarity.compareTwoStrings(
      targetQues.replace(/,/g, ""),
      transcript
    );

    if (listening) {
      toggleListening(); // 음성 인식을 멈춤
      setTimeout(() => resetTranscript(), 200); // 약간의 지연 후 초기화
    } else {
      resetTranscript();
    }
    setIsVoiceRecognitionRight(0);

    if (similarity >= 0.7) {
      setIsVoiceRecognitionRight(1);
      setNumberOfIncorrectNounQuestions(0);
      if (quesNum === numberOfQue - 1) {
        //한문장에 대해 문제를 다 순회하면
        setQuesNum(0); // 문제 번호를 0으로 초기화 (한 문장당 문제가 여러개 있음)
        setProblemType(2); // 그리고 다음 문제 단계로 넘어감
      } else {
        setQuesNum((prev) => prev + 1);
      }
    } else {
      //   if (quesNum === 1) {
      //     setQuesNum(0);
      //     return;
      //   }
      setNumberOfIncorrectNounQuestions((prev) => prev + 1);
      if (numberOfIncorrectNounQuestions == 2) {
        if (quesNum === numberOfQue - 1) {
          //한문장에 대해 문제를 다 순회하면
          setQuesNum(0); // 문제 번호를 0으로 초기화 (한 문장당 문제가 여러개 있음)
          setProblemType(2); // 그리고 다음 문제 단계로 넘어감
        } else {
          setQuesNum((prev) => prev + 1);
        }
        setNumberOfIncorrectNounQuestions(0);
      }
      setIsVoiceRecognitionRight(-1);
    }
  };

  useEffect(() => {
    if (quesNum === 0) {
      axios
        .get("https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts", {
          params: { ssml: `<speak>다음 명사를 차례대로 말해보세요"</speak>` },
        })
        .then((audioRes) => {
          const { audioContent } = audioRes.data;
          const audioBlob = base64ToBlob(audioContent, "audio/mp3");
          const audioUrl = URL.createObjectURL(audioBlob);

          // 오디오 요소 생성 및 바로 실행
          audioElement.current = new Audio(audioUrl);
          audioElement.current.play(); // 바로 오디오 실행
        });
    } else if (quesNum === 1) {
      axios
        .get("https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts", {
          params: {
            ssml: `<speak>방금 말한 명사를 거꾸로 말해보세요"</speak>`,
          },
        })
        .then((audioRes) => {
          const { audioContent } = audioRes.data;
          const audioBlob = base64ToBlob(audioContent, "audio/mp3");
          const audioUrl = URL.createObjectURL(audioBlob);

          // 오디오 요소 생성 및 바로 실행
          audioElement.current = new Audio(audioUrl);
          audioElement.current.play(); // 바로 오디오 실행
        });
    }
  }, [quesNum]);

  return (
    <div>
      {nounQues.length !== 0 ? (
        <div>
          {quesNum === 0 && (
            <div className="border border-slate-500 rounded-md">
              1. 다음 명사를 차례대로 말해보세요
            </div>
          )}
          {quesNum === 1 && (
            <div className="border border-slate-500 rounded-md">
              2. 방금 말한 명사를 거꾸로 말해보세요
            </div>
          )}
          <div>
            {quesNum === 0
              ? targetQues
              : targetQues.replace(/[\uAC00-\uD7A3]/g, "□")}
          </div>
          <div className="text-pink-500">
            {isVoiceRecognitionRight === 0
              ? ""
              : isVoiceRecognitionRight === 1
              ? "잘하셨어요!"
              : numberOfIncorrectNounQuestions === 2
              ? `힌트: 단어를 순서대로 읽어보세요!`
              : "틀렸습니다."}
          </div>
          <VoiceRecognitionBar
            listening={listening}
            transcript={transcript}
            toggleListening={toggleListening}
            onSubmitTranscript={onSubmitTranscript}
          />
        </div>
      ) : (
        "명사 문제 생성중..."
      )}
    </div>
  );
};

export default NounQues;
